/**
 * What kind of study a draft is, beyond one survey sent once
 * (docs/pdlc/builder.md, "Study types").
 *
 * Pure. A draft without `study` is a standard survey, or a store study when it
 * has `location`. With `study` it is one of the feasibility study types that
 * run over time or by exposure:
 *
 * - tracker: the same survey fielded in waves, new people each wave;
 * - longitudinal, diary_study, in_home_usage_test: the same people answer
 *   several phases, days apart. A question asks in every phase unless its
 *   `phases` names the ones it asks in (a recruit screener in phase 1, a
 *   follow-up in phase 2);
 * - ooh_ad_measurement: people near out-of-home placements and a control
 *   group, the audience's `ooh_exposure` criterion;
 * - repeat_diary: one survey each person answers again and again, up to
 *   `entries_max` times (SOTG's diary method). Staff only; feasibility checks
 *   it as a diary study (Builder draft Amendment 1, W2.4, decision 16).
 *
 * Interviews and focus groups are scheduled sessions, not surveys, and are
 * not Builder studies.
 */
import { z } from 'zod';

import { usesSensitiveProfile } from '@/lib/feasibility/engine/profile-fields';

import { addDays } from './dates';
import type {
    BuilderDraftContent,
    BuilderLocation,
    BuilderQuestion,
    LocationStudyType,
} from './draft';

export const STUDY_KINDS = [
    'tracker',
    'longitudinal',
    'diary_study',
    'in_home_usage_test',
    'ooh_ad_measurement',
    'repeat_diary',
] as const;
export type StudyKind = (typeof STUDY_KINDS)[number];

/** The same people answer more than once. */
export const PHASED_STUDY_KINDS = ['longitudinal', 'diary_study', 'in_home_usage_test'] as const;
export type PhasedStudyKind = (typeof PHASED_STUDY_KINDS)[number];

/** Bounds shared with the feasibility request (phases, daysBetweenPhases, productsTested). */
export const STUDY_BOUNDS = {
    waves: { min: 2, max: 52 },
    daysBetweenWaves: { min: 1, max: 365 },
    phases: { min: 2, max: 12 },
    daysBetweenPhases: { min: 1, max: 120 },
    productsTested: { min: 1, max: 10 },
    /** As SOTG stores a diary's limits (tinyint unsigned); 0 entries is no limit. */
    entriesMax: { min: 0, max: 255 },
    entriesPerDay: { min: 1, max: 255 },
    /** SOTG keeps a reminder's wait in minutes in a smallint: up to 1,092 hours. */
    reminderHours: { min: 1, max: 1092 },
} as const;

const int = (b: { min: number; max: number }) => z.number().int().min(b.min).max(b.max);

export const BuilderStudySchema = z
    .object({
        kind: z.enum(STUDY_KINDS),
        /** Tracker: how many waves, and days from one wave's start to the next. */
        waves: int(STUDY_BOUNDS.waves).optional(),
        days_between_waves: int(STUDY_BOUNDS.daysBetweenWaves).optional(),
        /** Phased studies: how many times the same people answer, and the wait between. */
        phases: int(STUDY_BOUNDS.phases).optional(),
        days_between_phases: int(STUDY_BOUNDS.daysBetweenPhases).optional(),
        /** In-home usage test: products each person is sent. */
        products_tested: int(STUDY_BOUNDS.productsTested).optional(),
        /** Repeat diary: entries each person may make (0: no limit), at most per day, and "#n" on each entry. */
        entries_max: int(STUDY_BOUNDS.entriesMax).optional(),
        entries_per_day: int(STUDY_BOUNDS.entriesPerDay).optional(),
        number_entries: z.boolean().optional(),
        /** Repeat diary: hours after an entry to remind the person, twice at most, and to close the next entry. */
        reminder_hours: int(STUDY_BOUNDS.reminderHours).optional(),
        second_reminder_hours: int(STUDY_BOUNDS.reminderHours).optional(),
        expires_hours: int(STUDY_BOUNDS.reminderHours).optional(),
        /** The push the reminders send. */
        reminder_text: z.string().trim().min(1).max(1024).optional(),
    })
    .strict();
export type BuilderStudy = z.infer<typeof BuilderStudySchema>;

/** Where each study kind starts, from the feasibility study types' defaults. */
/** How a store-location survey starts, by kind (the editor's Store locations switch uses the same). */
export const LOCATION_STARTING: Record<
    LocationStudyType,
    Pick<BuilderLocation, 'push' | 'invite_minutes'>
> = {
    in_store_intercept: { push: 'at_store', invite_minutes: 60 },
    exit_interview: { push: 'after_visit', invite_minutes: 60 },
    exit_interview_24h: { push: 'after_visit', invite_minutes: 1440 },
    directed_shop: { push: 'no_push', invite_minutes: 1440 },
};

export const STUDY_STARTING: Record<StudyKind, BuilderStudy> = {
    tracker: { kind: 'tracker', waves: 4, days_between_waves: 30 },
    longitudinal: { kind: 'longitudinal', phases: 3, days_between_phases: 30 },
    diary_study: { kind: 'diary_study', phases: 5, days_between_phases: 1 },
    in_home_usage_test: {
        kind: 'in_home_usage_test',
        phases: 2,
        days_between_phases: 7,
        products_tested: 1,
    },
    ooh_ad_measurement: { kind: 'ooh_ad_measurement' },
    repeat_diary: { kind: 'repeat_diary', entries_max: 7 },
};

/** SOTG's survey method for a repeat diary (`surveyMethods.id` 3); every other Builder survey is 1. */
export const REPEAT_DIARY_METHOD_ID = 3;

/** A repeat diary's reminders and expiry as the launch sends them, in SOTG's minutes. */
export function diaryReminders(study: BuilderStudy): {
    firstReminderMinutes?: number;
    secondReminderMinutes?: number;
    expiresAfterMinutes?: number;
    reminderText?: string;
} {
    return {
        ...(study.reminder_hours ? { firstReminderMinutes: study.reminder_hours * 60 } : {}),
        ...(study.second_reminder_hours
            ? { secondReminderMinutes: study.second_reminder_hours * 60 }
            : {}),
        ...(study.expires_hours ? { expiresAfterMinutes: study.expires_hours * 60 } : {}),
        ...(study.reminder_text ? { reminderText: study.reminder_text } : {}),
    };
}

/** How many entries a repeat diary allows: 0 is no limit. */
export const diaryEntries = (study: BuilderStudy): number =>
    study.entries_max ?? STUDY_STARTING.repeat_diary.entries_max!;

export const isPhased = (
    study: BuilderStudy | undefined,
): study is BuilderStudy & { kind: PhasedStudyKind } =>
    !!study && (PHASED_STUDY_KINDS as readonly string[]).includes(study.kind);

/** How many phases the draft has: 1 unless it is a phased study. */
export function phaseCount(draft: Pick<BuilderDraftContent, 'study'>): number {
    return isPhased(draft.study)
        ? (draft.study.phases ?? STUDY_STARTING[draft.study.kind].phases!)
        : 1;
}

/** The phases a question asks in, in order. Absent `phases` is every phase. */
export function phasesOf(q: Pick<BuilderQuestion, 'phases'>, count: number): number[] {
    const all = Array.from({ length: count }, (_, i) => i + 1);
    if (!q.phases) return all;
    return all.filter((p) => q.phases!.includes(p));
}

/**
 * Whether `source`'s answer is known when `target` is asked in phase `p`:
 * asked earlier in the same phase, or in any earlier phase.
 */
export function answerKnownAt(
    source: { index: number; phases: number[] },
    target: { index: number },
    p: number,
): boolean {
    if (source.phases.some((s) => s < p)) return true;
    return source.phases.includes(p) && source.index < target.index;
}

/** Ages under 18 anywhere in the audience: the criteria or the quota cells. */
function targetsYouth(draft: Pick<BuilderDraftContent, 'audience'>): boolean {
    const byCriteria = draft.audience.criteria.some(
        (c) => c.kind === 'demographic' && !!c.age && c.age.min < 18,
    );
    const byQuota = (draft.audience.quotas?.dimensions ?? []).some(
        (d) => d.kind === 'age' && d.cells.some((cell) => !!cell.age && cell.age.min < 18),
    );
    return byCriteria || byQuota;
}

/**
 * Why a draft's targeting is legally sensitive (teens, tobacco use, young
 * children), or an empty list. A sensitive survey is never launched without a
 * manual review, and only staff launch one.
 */
export function sensitiveTargeting(draft: Pick<BuilderDraftContent, 'audience'>): string[] {
    const out: string[] = [];
    if (targetsYouth(draft)) out.push('Teens (13–17)');
    out.push(...usesSensitiveProfile(draft.audience.criteria));
    return out;
}

export interface WaveWindow {
    wave: number;
    /** ISO days. */
    start: string;
    end: string;
}

/**
 * When each wave fields, from the launch's start day: a tracker's waves are
 * `days_between_waves` apart and each runs for the field days; anything else is
 * one window. Null field days means one day per wave.
 */
export function waveSchedule(
    draft: Pick<BuilderDraftContent, 'study' | 'fielding'>,
    startDay: string,
): WaveWindow[] {
    const waves = waveCount(draft);
    const days = Math.max(1, draft.fielding.fieldDays ?? 1);
    const gap =
        draft.study?.kind === 'tracker'
            ? (draft.study.days_between_waves ?? STUDY_STARTING.tracker.days_between_waves!)
            : 0;
    return Array.from({ length: waves }, (_, i) => {
        const start = addDays(startDay, i * gap);
        return { wave: i + 1, start, end: addDays(start, days - 1) };
    });
}

/** How many waves a tracker runs: 1 for anything else. */
export function waveCount(draft: Pick<BuilderDraftContent, 'study'>): number {
    return draft.study?.kind === 'tracker'
        ? (draft.study.waves ?? STUDY_STARTING.tracker.waves!)
        : 1;
}

/**
 * What the rate card prices. A tracker's target is per wave, so its completes
 * are the target times the waves; a phased study's target is per phase, so its
 * completes are the target times the phases (Amendment 1, decision 22). An
 * out-of-home study has no price in Builder: the team prices it.
 */
export function priceBasis(
    draft: Pick<BuilderDraftContent, 'study' | 'fielding'>,
): { priced: true; completes: number | null } | { priced: false; label: string } {
    // A repeat diary's target counts every entry, as SOTG counts its completes, so it
    // is priced as a survey of that many completes.
    if (draft.study?.kind === 'ooh_ad_measurement') {
        return { priced: false, label: 'Priced by your MFour team' };
    }
    const target = draft.fielding.target;
    const times = isPhased(draft.study) ? phaseCount(draft) : waveCount(draft);
    return { priced: true, completes: target === null ? null : target * times };
}

/** The questions asked in phase `p`, in survey order (Preview walks one phase at a time). */
export function phaseQuestions(
    draft: Pick<BuilderDraftContent, 'study' | 'questions'>,
    p: number,
): BuilderQuestion[] {
    const count = phaseCount(draft);
    return count === 1
        ? draft.questions
        : draft.questions.filter((q) => phasesOf(q, count).includes(p));
}

/**
 * The phases of a version that no preview has walked, in order; empty means
 * launch may go ahead. A person in the editor or an agent through the API
 * counts the same, and so does a walk that was screened out.
 */
export function unpreviewedPhases(
    draft: Pick<BuilderDraftContent, 'study'>,
    previews: readonly { phase: number }[],
): number[] {
    const walked = new Set(previews.map((p) => p.phase));
    return Array.from({ length: phaseCount(draft) }, (_, i) => i + 1).filter((p) => !walked.has(p));
}

export type StaffOnlyCode =
    | 'LOCATION_STAFF_ONLY'
    | 'STUDY_STAFF_ONLY'
    | 'SENSITIVE_STAFF_ONLY'
    | 'LIST_STAFF_ONLY';

/**
 * Why only staff may publish or launch this survey, or null. The one rule the
 * publish and launch routes enforce and the editor shows ahead of time.
 */
export function staffOnly(
    draft: Pick<BuilderDraftContent, 'location' | 'study' | 'audience'>,
): { code: StaffOnlyCode; subject: string } | null {
    if (draft.location) return { code: 'LOCATION_STAFF_ONLY', subject: 'run at store locations' };
    if (draft.study)
        return {
            code: 'STUDY_STAFF_ONLY',
            subject: 'run in waves, phases or at out-of-home placements',
        };
    if (sensitiveTargeting(draft).length)
        return {
            code: 'SENSITIVE_STAFF_ONLY',
            subject: 'target teens, tobacco use or young children',
        };
    // Fielded from release groups made from the feasibility check (list-target.ts).
    if (
        draft.audience.criteria.some(
            (c) =>
                c.kind === 'demographic' &&
                (!!c.zips?.codes.length || (!!c.panelists?.ids.length && !c.panelists.exclude)),
        )
    )
        return {
            code: 'LIST_STAFF_ONLY',
            subject: 'are sent to a zip list or a list of panelists',
        };
    return null;
}

/** "Your MFour team launches surveys that run at store locations." */
export const staffOnlyMessage = (gate: { subject: string }, step: 'publish' | 'launch'): string =>
    `Your MFour team ${step === 'publish' ? 'sets up' : 'reviews and launches'} surveys that ${gate.subject}.`;

/** One line naming the study, for the editor's header and checklist. */
export function studyLabel(draft: Pick<BuilderDraftContent, 'study' | 'location'>): string {
    const s = draft.study;
    const at = draft.location ? ' at stores' : '';
    if (!s) return draft.location ? 'Store survey' : 'One-time survey';
    switch (s.kind) {
        case 'tracker':
            return `Tracker${at} · ${waveCount(draft)} waves, ${s.days_between_waves ?? STUDY_STARTING.tracker.days_between_waves} days apart`;
        case 'longitudinal':
            return `Longitudinal${at} · ${phaseCount(draft)} phases`;
        case 'diary_study':
            return `Diary study · ${phaseCount(draft)} entries`;
        case 'in_home_usage_test':
            return `In-home usage test · ${phaseCount(draft)} phases`;
        case 'ooh_ad_measurement':
            return 'Out-of-home ad measurement';
        case 'repeat_diary': {
            const n = diaryEntries(s);
            return `Repeat diary · ${n === 0 ? 'no entry limit' : `up to ${n} entries`}`;
        }
    }
}
